import { Paperclip } from 'lucide-react';
import type { PortalMessage } from '@/types';

interface MessageBubbleProps {
  message: PortalMessage;
  isRequester: boolean;
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function MessageBubble({ message, isRequester }: MessageBubbleProps) {
  const attachments = message.attachments
    ? (typeof message.attachments === 'string' ? JSON.parse(message.attachments) : message.attachments)
    : [];

  return (
    <div className={`flex ${isRequester ? 'justify-end' : 'justify-start'}`}>
      <div className={`${isRequester ? 'order-last' : ''}`}>
        {/* Sender info */}
        <div className={`flex items-center gap-2 mb-1 ${isRequester ? 'justify-end' : ''}`}>
          <span className="text-xs font-medium text-foreground">
            {isRequester ? 'You' : (message.sender_name || 'Support')}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatDateTime(message.sent_at)}
          </span>
        </div>

        {/* Message body */}
        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            isRequester
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground'
          }`}
        >
          {message.body ? (
            <div
              className={`prose prose-sm max-w-none [&>p]:m-0 [&>br]:leading-relaxed ${
                isRequester
                  ? 'text-white [&_*]:text-white [&_a]:underline'
                  : 'dark:prose-invert'
              }`}
              dangerouslySetInnerHTML={{ __html: message.body }}
            />
          ) : (
            <p className="whitespace-pre-wrap">{message.body_plain || ''}</p>
          )}
        </div>

        {/* Attachments */}
        {Array.isArray(attachments) && attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {attachments.map((att: any, idx: number) => (
              <div key={idx} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Paperclip className="h-3 w-3" />
                <span>{att.name || att.filename || `Attachment ${idx + 1}`}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
